/**
 * A single row in the tracked-keywords table.
 *
 * Extracted from tracking-panel.tsx to keep that file under budget after
 * H3 (provider on rank points + GSC-vs-SERP disagreement tooltip).
 */
import {
  ArrowDown,
  ArrowUp,
  HelpCircle,
  Loader2,
  Minus,
  Trash2,
} from 'lucide-react';
import type { TrackedKeyword } from '@seo/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { relTime } from '@/lib/format';
import { useGscInsightsForKeyword } from '@/features/integrations/use-gsc-insights';

/** How many positions apart before we bother flagging GSC ≠ SERP. */
const GSC_SERP_DISAGREE_THRESHOLD = 2;

export function KeywordRow({
  projectId,
  row,
  awaiting,
  deleting,
  onDelete,
}: {
  projectId: string;
  row: TrackedKeyword;
  awaiting: boolean;
  deleting: boolean;
  onDelete: () => void;
}): JSX.Element {
  const { opportunity: gsc } = useGscInsightsForKeyword(projectId, row.keyword);
  const serpPos = row.current_position ?? null;
  const gscPos = gsc?.position ?? null;
  const showDisagreement =
    serpPos != null &&
    gscPos != null &&
    Math.abs(gscPos - serpPos) >= GSC_SERP_DISAGREE_THRESHOLD;

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{row.keyword}</span>
          {showDisagreement ? (
            <GscSerpDisagreementHint
              provider={row.last_provider}
              serpPosition={serpPos!}
              gscPosition={gscPos!}
            />
          ) : null}
        </div>
        {row.url_that_ranked ? (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {row.url_that_ranked}
          </div>
        ) : row.target_url ? (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            target: {row.target_url}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3 tabular-nums">
        {awaiting ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : row.current_position != null ? (
          <PositionCell
            position={row.current_position}
            provider={row.last_provider}
            checkedAt={row.last_checked_at}
          />
        ) : (
          <NoRankCell
            checkedAt={row.last_checked_at}
            provider={row.last_provider}
          />
        )}
      </td>
      <td className="px-4 py-3 tabular-nums">
        <PreviousCell
          previousPosition={row.previous_position ?? null}
          previousCheckedAt={row.previous_checked_at ?? null}
          changeSinceLastCheck={row.change_since_last_check ?? null}
        />
      </td>
      <td className="px-4 py-3">
        <Delta value={row.change_7d} />
      </td>
      <td className="px-4 py-3">
        <Delta value={row.change_30d} />
      </td>
      <td className="px-4 py-3">
        <ProviderBadge
          provider={row.last_provider}
          checkedAt={row.last_checked_at}
        />
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {awaiting
          ? 'Checking…'
          : row.last_checked_at
            ? relTime(row.last_checked_at)
            : 'Never'}
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          variant="ghost"
          size="icon"
          disabled={deleting || awaiting}
          aria-label={`Stop tracking ${row.keyword}`}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function PositionCell({
  position,
  provider,
  checkedAt,
}: {
  position: number;
  provider?: string | null;
  checkedAt: string | null;
}): JSX.Element {
  const providerLabel = formatProvider(provider);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
          #{position}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          <div>
            SERP position via <span className="font-medium">{providerLabel}</span>
          </div>
          {checkedAt ? (
            <div className="text-muted-foreground">Checked {relTime(checkedAt)}</div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function NoRankCell({
  checkedAt,
  provider,
}: {
  checkedAt: string | null;
  provider?: string | null;
}): JSX.Element {
  if (!checkedAt) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Not checked yet — click <span className="font-medium">Check now</span> to fetch a position.
        </TooltipContent>
      </Tooltip>
    );
  }
  const providerLabel = formatProvider(provider);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help whitespace-nowrap text-xs font-medium text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
          Not in top 100
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-1">
          <div>
            We checked via <span className="font-medium">{providerLabel}</span>{' '}
            {relTime(checkedAt)} — your site was not in Google&apos;s first 100
            results for this keyword.
          </div>
          <div className="text-muted-foreground">
            Try a keyword you already rank for, or strengthen the page with a
            brief + fresh content.
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function ProviderBadge({
  provider,
  checkedAt,
}: {
  provider?: string | null;
  checkedAt?: string | null;
}): JSX.Element {
  if (!provider) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const label = formatProvider(provider);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className="cursor-help font-normal capitalize">
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div>
          Last rank check ran on{' '}
          <span className="font-medium">{label}</span>
          {checkedAt ? ` (${relTime(checkedAt)})` : ''}.
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function GscSerpDisagreementHint({
  provider,
  serpPosition,
  gscPosition,
}: {
  provider?: string | null;
  serpPosition: number;
  gscPosition: number;
}): JSX.Element {
  const providerLabel = formatProvider(provider);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Why SERP position and GSC average differ"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-1">
          <div>
            SERP (via <span className="font-medium">{providerLabel}</span>):
            position #{serpPosition}
          </div>
          <div>
            GSC (28d avg): position #{gscPosition.toFixed(1)}
          </div>
          <div className="pt-0.5 text-muted-foreground">
            These can disagree — SERP is a snapshot; GSC is an average across
            queries and locations.
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function formatProvider(provider?: string | null): string {
  if (!provider) return 'Unknown';
  if (provider === 'dataforseo') return 'DataForSEO';
  if (provider === 'serper') return 'Serper';
  return provider;
}

function PreviousCell({
  previousPosition,
  previousCheckedAt,
  changeSinceLastCheck,
}: {
  previousPosition: number | null;
  previousCheckedAt: string | null;
  changeSinceLastCheck: number | null;
}): JSX.Element {
  if (previousPosition == null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground">—</span>
        </TooltipTrigger>
        <TooltipContent>
          No prior check on record. Once a second rank check runs, this shows
          the position from the check before the latest.
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">
          <span className="underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
            #{previousPosition}
          </span>
          {changeSinceLastCheck != null && changeSinceLastCheck !== 0 ? (
            <span className="ml-1.5 text-xs">
              <Delta value={changeSinceLastCheck} />
            </span>
          ) : null}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          <div>
            Previous position:{' '}
            <span className="font-medium">#{previousPosition}</span>
          </div>
          {previousCheckedAt ? (
            <div className="text-muted-foreground">
              Checked {relTime(previousCheckedAt)}
            </div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function Delta({ value }: { value: number | null | undefined }): JSX.Element {
  if (value == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
        <Minus className="h-3.5 w-3.5" />0
      </span>
    );
  }
  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
        <ArrowUp className="h-3.5 w-3.5" />+{value}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-rose-600 dark:text-rose-400">
      <ArrowDown className="h-3.5 w-3.5" />
      {value}
    </span>
  );
}
